import fork from '@haaretz/l-fork.macro';
import discardNullableValues from '@haaretz/s-common-utils/discardNullableValues';
import { currency } from '@haaretz/s-consts';
import isValidSelectedOffer from '@haaretz/s-type-predicates/isValidSelectedOffer';

import type { SelectedOfferProps } from '@haaretz/s-data-structure-types';
import type { PurchaseKind } from '@haaretz/s-types';
import type { PPDataStoreDebtContextProps } from '@haaretz/s-use-pp-data-store';

export interface UtmHtmProps {
  utmSource?: string | string[];
  utmMedium?: string | string[];
  utmCampaign?: string | string[];
  utmContent?: string | string[];
}

export type DonationParams = {
  firstName: string;
  lastName: string;
  email: string;
  phone?: number;
};
export type CommonPaymentParameters = PPDataStoreDebtContextProps &
  Partial<SelectedOfferProps> &
  UtmHtmProps & {
    couponId?: string | null;
    personalCouponId?: string | null;
  } & (
    | {
        kind?: Extract<PurchaseKind, 'payment'>;
        donationParams?: never;
      }
    | {
        kind: Extract<PurchaseKind, 'donation'>;
        donationParams: DonationParams;
      }
  );

export default function getCommonParameters(props: CommonPaymentParameters) {
  const { kind = 'payment' } = props;
  const isPayment = kind === 'payment';

  if (!isValidSelectedOffer(props) && isPayment) {
    throw new Error('Offer not selected');
  }

  const optionalParams = {
    utmCampaign: props.utmCampaign,
    utmContent: props.utmContent,
    utmMedium: props.utmMedium,
    utmSource: props.utmSource,
    couponId: props.couponId,
    personalCouponId: props.personalCouponId,
    firstName: props.donationParams?.firstName,
    lastName: props.donationParams?.lastName,
    email: props.donationParams?.email,
    phone: props.donationParams?.phone?.toString(),
  };

  const params = new URLSearchParams(
    discardNullableValues({
      cgtype: fork({ default: 'heb', hdc: 'eng' }),
      ...(isPayment && {
        currency: props.currency || currency,
        contentId: props.contentId,
        productId: props.productNumber?.toString(),
        saleCode: props.saleCode?.toString(),
        promotionNumber: props.promotionNumber?.toString(),
        approveDebtClaim: String(props.approveDebtClaim === true),
        thankYouEmailTemplate: props.thankYouMailTemplate,
      }),
    })
  );

  (Object.keys(optionalParams) as (keyof typeof optionalParams)[]).forEach(name => {
    const value = optionalParams[name];

    if (value) {
      params.append(name, value.toString());
    }
  });

  return params;
}
